import React from 'react';
import SafeIcon from '../common/SafeIcon';
import { Link } from 'react-router-dom';
import { ASSET_CONFIG } from '../config';
import { FiMenu, FiBell, FiSearch, FiUser, FiHelpCircle, FiAward, FiLogOut } from 'react-icons/fi';

const Header = ({ sidebarOpen, setSidebarOpen, title, user, onLogout }) => {
  const getTitle = (id) => {
    const titles = {
      'introduction': 'Dashboard',
      'nouns': 'Nouns & Gender',
      'pronouns': 'Pronouns',
      'cultural': 'Cultural Context',
      'exercises': 'Interactive Exercises',
      'audio': 'Audio Gallery',
      'reflection': 'Quiz & Reflection',
      'profile': 'Student Profile',
      'certificate': 'Certificate',
      'syllabus': 'Course Syllabus',
      'teacher-hub': 'Teacher & Parent Hub'
    };
    return titles[id] || 'Dashboard';
  };

  return (
    <header className="h-20 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 px-6 lg:px-8 flex items-center justify-between sticky top-0 z-30 shadow-sm">
      {/* Left Section */}
      <div className="flex items-center gap-4">
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="p-3 -ml-3 text-slate-500 hover:text-slate-700 lg:hidden rounded-xl hover:bg-slate-100 transition-all"
        >
          <SafeIcon icon={FiMenu} className="text-xl" />
        </button>
        <div>
          <h2 className="text-2xl font-bold text-transparent bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text capitalize hidden md:block">
            {getTitle(title)}
          </h2>
          <p className="text-sm text-slate-500 mt-0.5">Continue your learning journey</p>
        </div>
      </div>

      {/* Center Search */}
      <div className="hidden md:flex flex-1 max-w-lg mx-8">
        <div className="relative w-full">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg" />
          <input 
            type="text" 
            placeholder="Search lessons, topics, or keywords..." 
            className="w-full pl-12 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all text-slate-700 placeholder-slate-400"
          />
        </div>
      </div>

      {/* Right Section */}
      <div className="flex items-center gap-2 md:gap-4">
        {/* Help Button */}
        <button className="p-3 text-slate-400 hover:text-slate-600 transition-colors relative group">
          <SafeIcon icon={FiHelpCircle} className="text-lg" />
          <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 p-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            <h4 className="text-sm font-bold text-gray-800 mb-2">Need Help?</h4>
            <div className="text-xs text-slate-600 mb-3">
              Contact our student support team for assistance.
            </div>
            <a 
              href={`mailto:${ASSET_CONFIG.SUPPORT_EMAIL}`}
              className="block w-full text-center py-2 px-3 bg-slate-50 hover:bg-slate-100 text-green-700 rounded-lg text-xs font-bold transition-colors border border-slate-200"
            >
              {ASSET_CONFIG.SUPPORT_EMAIL}
            </a>
          </div>
        </button>

        {/* Notifications */}
        <button className="relative p-3 text-slate-400 hover:text-slate-600 transition-colors">
          <SafeIcon icon={FiBell} className="text-lg" />
          <div className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></div>
        </button>

        {/* User Profile */}
        <div className="flex items-center gap-3 pl-3 border-l border-slate-200 hover:bg-slate-50 rounded-xl transition-colors p-1 group relative cursor-pointer">
          <Link to="/profile" className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center text-white font-bold shadow-lg text-xl">
                {user ? user.avatar : 'S'}
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 border-2 border-white rounded-full"></div>
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-semibold text-slate-700 leading-none">{user ? user.name : 'Guest'}</p>
              <div className="flex items-center gap-1 mt-0.5">
                <SafeIcon icon={FiAward} className="text-xs text-amber-500" />
                <p className="text-xs text-slate-500">Level 1 • {user ? user.xp : 0} XP</p>
              </div>
            </div>
          </Link>
          
          {/* User Dropdown */}
          <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 p-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
            <Link to="/profile" className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 rounded-lg">
              <SafeIcon icon={FiUser} /> Profile
            </Link>
            <button 
              onClick={onLogout}
              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <SafeIcon icon={FiLogOut} /> Switch User
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;